import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Switch } from "../components/ui/switch";
import { isSameDay, format } from "date-fns";

// Sub-components
import { CalendarHeader } from "../components/calendar/CalendarHeader";
import { EventList } from "../components/calendar/EventList";
import { UpcomingEvents } from "../components/calendar/UpcomingEvents";
import { GoogleSettingsDialog } from "../components/calendar/GoogleSettingsDialog";
import { EventFormDialog } from "../components/calendar/EventFormDialog";

export default function CalendarPage() {
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showGoogleSettings, setShowGoogleSettings] = useState(false);

  // Google Calendar state
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState("");
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [showGoogleEvents, setShowGoogleEvents] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: new Date(),
    time: "",
    reminder: false,
    reminder_time: "30min",
    participants: [],
    color: "#6366f1",
  });

  const fetchGoogleData = useCallback(async () => {
    try {
      const [calendarsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/google/calendars`),
        axios.get(`${API}/google/events`),
      ]);

      setGoogleCalendars(calendarsRes.data.calendars || []);
      setGoogleEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error("Failed to fetch Google Calendar data:", error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, membersRes, googleStatusRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/members`),
        axios
          .get(`${API}/oauth/calendar/status`)
          .catch(() => ({ data: { connected: false } })),
      ]);

      setEvents(eventsRes.data);
      setMembers(membersRes.data);

      if (googleStatusRes.data.connected) {
        setGoogleConnected(true);
        setGoogleEmail(googleStatusRes.data.email);
        fetchGoogleData();
      }
    } catch (error) {
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  }, [fetchGoogleData]);

  useEffect(() => {
    const connected = searchParams.get("connected");
    const email = searchParams.get("email");
    const error = searchParams.get("error");

    if (connected === "true" && email) {
      toast.success(`Connected to Google Calendar as ${email}`);
      setGoogleConnected(true);
      setGoogleEmail(email);
      window.history.replaceState({}, "", "/calendar");
    }

    if (error) {
      toast.error(`Failed to connect: ${error}`);
      window.history.replaceState({}, "", "/calendar");
    }

    fetchData();
  }, [searchParams, fetchData]);

  const connectGoogleCalendar = async () => {
    try {
      const res = await axios.get(`${API}/oauth/calendar/login`);
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error("Failed to start Google Calendar connection");
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.delete(`${API}/oauth/calendar/disconnect`);
      setGoogleConnected(false);
      setGoogleEmail("");
      setGoogleCalendars([]);
      setGoogleEvents([]);
      toast.success("Google Calendar disconnected");
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const toggleCalendarSync = async (calendarId, currentlySynced) => {
    const currentSynced = googleCalendars.filter((c) => c.synced).map((c) => c.id);
    const newSynced = currentlySynced
      ? currentSynced.filter((id) => id !== calendarId)
      : [...currentSynced, calendarId];

    try {
      await axios.post(`${API}/google/calendars/sync`, newSynced);
      setGoogleCalendars((prev) =>
        prev.map((cal) =>
          cal.id === calendarId ? { ...cal, synced: !currentlySynced } : cal
        )
      );
      const eventsRes = await axios.get(`${API}/google/events`);
      setGoogleEvents(eventsRes.data.events || []);
      toast.success(
        currentlySynced ? "Calendar removed from sync" : "Calendar added to sync"
      );
    } catch (error) {
      toast.error("Failed to update sync settings");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Event title is required");
      return;
    }

    try {
      const data = {
        ...formData,
        date: format(formData.date, "yyyy-MM-dd"),
        reminder_time: formData.reminder ? formData.reminder_time : null,
      };

      if (editingEvent) {
        const res = await axios.put(`${API}/events/${editingEvent.id}`, data);
        setEvents((prev) =>
          prev.map((e) => (e.id === editingEvent.id ? res.data : e))
        );
        toast.success("Event updated");
      } else {
        const res = await axios.post(`${API}/events`, data);
        setEvents((prev) => [res.data, ...prev]);
        toast.success("Event created");
      }

      closeModal();
    } catch (error) {
      toast.error("Failed to save event");
    }
  };

  const deleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success("Event deleted");
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || "",
      date: new Date(event.date),
      time: event.time || "",
      reminder: event.reminder,
      reminder_time: event.reminder_time || "30min",
      participants: event.participants || [],
      color: event.color,
    });
    setModalOpen(true);
  };

  const openNewEventModal = (date = selectedDate) => {
    setEditingEvent(null);
    setFormData({
      title: "",
      description: "",
      date: date,
      time: "",
      reminder: false,
      reminder_time: "30min",
      participants: [],
      color: "#6366f1",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingEvent(null);
  };

  // Combine local and Google events
  const allEvents = [...events, ...(showGoogleEvents ? googleEvents : [])];
  const selectedDateEvents = allEvents.filter((event) =>
    isSameDay(new Date(event.date), selectedDate)
  );
  const eventDates = allEvents.map((e) => new Date(e.date));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <CalendarHeader
        selectedDate={selectedDate}
        googleConnected={googleConnected}
        onConnectGoogle={connectGoogleCalendar}
        onShowGoogleSettings={() => setShowGoogleSettings(true)}
        onAddEvent={() => openNewEventModal()}
      />

      {/* Google Events Toggle */}
      {googleConnected && googleEvents.length > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50">
          <Switch
            checked={showGoogleEvents}
            onCheckedChange={setShowGoogleEvents}
            data-testid="toggle-google-events"
          />
          <span className="text-slate-300">
            Show Google Calendar events ({googleEvents.length})
          </span>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Calendar Widget */}
        <div className="lg:col-span-7 card-base p-6">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && setSelectedDate(date)}
            className="w-full"
            modifiers={{ hasEvent: eventDates }}
            modifiersStyles={{
              hasEvent: {
                backgroundColor: "rgba(99, 102, 241, 0.2)",
                borderRadius: "50%",
              },
            }}
            data-testid="calendar-widget"
          />
        </div>

        {/* Events for Selected Date */}
        <EventList
          selectedDate={selectedDate}
          events={selectedDateEvents}
          onAddEvent={openNewEventModal}
          onEditEvent={openEditModal}
          onDeleteEvent={deleteEvent}
        />
      </div>

      {/* Upcoming Events */}
      <UpcomingEvents events={allEvents} onSelectDate={setSelectedDate} />

      {/* Google Calendar Settings Modal */}
      <GoogleSettingsDialog
        open={showGoogleSettings}
        onOpenChange={setShowGoogleSettings}
        googleEmail={googleEmail}
        calendars={googleCalendars}
        onToggleCalendarSync={toggleCalendarSync}
        onDisconnect={disconnectGoogleCalendar}
      />

      {/* Event Form Modal */}
      <EventFormDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        isEditing={!!editingEvent}
      />
    </div>
  );
}
